<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';
import { Letter } from 'vue-letter';
import { allowedCssProperties } from 'lettersanitizer';
import { EmailQuoteExtractor } from 'dashboard/helper/emailQuoteExtractor.js';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { ensureEmailContrast } from './emailContrast';

// Aceleres — uma mensagem da conversa de e-mail. Como no Gmail, as mensagens
// antigas ficam recolhidas (remetente + prévia) e abrem com um clique.
const props = defineProps({
  message: { type: Object, required: true },
  expanded: { type: Boolean, default: false },
  inboxEmail: { type: String, default: '' },
});

const emit = defineEmits(['toggle']);
const { t, locale } = useI18n();
const showQuoted = ref(false);
const bodyRef = ref(null);
const previewImage = ref(null);
let printFrame = null;

const letterCss = [...allowedCssProperties, 'transform', 'transform-origin'];

const isOutgoing = computed(() => props.message.direction === 'outgoing');

const fromLabel = computed(() => {
  if (isOutgoing.value) {
    return props.message.sender_name || props.message.from?.[0] || '';
  }
  return props.message.from?.[0] || t('INBOX.EMAIL.UNKNOWN_SENDER');
});

const recipientsLabel = computed(() => {
  const to = (props.message.to || []).map(address =>
    address === props.inboxEmail ? t('INBOX.EMAIL.ME') : address
  );
  return to.join(', ');
});

const sentAt = computed(() => {
  const timestamp = Number(props.message.sent_at || 0);
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const language = locale.value?.replace('_', '-') || 'pt-BR';
  const time = { hour: '2-digit', minute: '2-digit' };
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(language, time);
  }
  // No celular a data longa espremia o nome do remetente.
  return date.toLocaleString(language, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    ...time,
  });
});

// Incoming HTML is untrusted, including messages delivered to Spam. Sanitize
// before quote extraction and let vue-letter apply its own allowlist again.
const fullHtml = computed(() =>
  DOMPurify.sanitize(props.message.html || '', {
    USE_PROFILES: { html: true },
    FORBID_TAGS: [
      'script',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'button',
      'meta',
      'base',
      'link',
    ],
    FORBID_ATTR: ['srcdoc'],
  })
);
const hasQuotes = computed(() => EmailQuoteExtractor.hasQuotes(fullHtml.value));
const visibleHtml = computed(() =>
  showQuoted.value || !hasQuotes.value
    ? fullHtml.value
    : EmailQuoteExtractor.extractQuotes(fullHtml.value)
);

const preview = computed(() => {
  const container = document.createElement('div');
  container.innerHTML = EmailQuoteExtractor.extractQuotes(fullHtml.value);
  return (container.textContent || '').replace(/\s+/g, ' ').trim();
});

const attachmentName = attachment =>
  attachment.file_name || t('INBOX.EMAIL.ATTACHMENTS');
const isImage = attachment =>
  attachment.content_type?.startsWith('image/') ||
  attachment.file_type === 'image';
const imageAttachments = computed(() =>
  (props.message.attachments || []).filter(isImage)
);
const otherAttachments = computed(() =>
  (props.message.attachments || []).filter(attachment => !isImage(attachment))
);

watch(
  [visibleHtml, () => props.expanded],
  async () => {
    if (!props.expanded) return;
    await nextTick();
    ensureEmailContrast(bodyRef.value?.querySelector('.letter-render'));
  },
  { immediate: true, flush: 'post' }
);

const closePreview = () => {
  previewImage.value = null;
  printFrame?.remove();
  printFrame = null;
};
const onKeydown = event => {
  if (event.key === 'Escape') closePreview();
};
watch(previewImage, image => {
  if (image) document.addEventListener('keydown', onKeydown);
  else document.removeEventListener('keydown', onKeydown);
});
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
  printFrame?.remove();
});

const printImage = async () => {
  const attachment = previewImage.value;
  if (!attachment?.data_url) return;

  printFrame?.remove();
  const frame = document.createElement('iframe');
  printFrame = frame;
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText =
    'position:fixed;left:-10000px;top:0;width:1px;height:1px;border:0';
  document.body.appendChild(frame);

  const printDocument = frame.contentDocument;
  const fileName = attachmentName(attachment);
  printDocument.title = fileName;
  const style = printDocument.createElement('style');
  style.textContent = `
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; }
    body { display: grid; grid-template-rows: auto minmax(0, 1fr) auto;
      width: 210mm; height: 297mm; margin: 0; padding: 10mm 12mm 8mm;
      color: #202124; font: 9pt Arial, sans-serif; }
    header { display: grid; grid-template-columns: 1fr minmax(0, 2fr) 1fr;
      align-items: center; padding-bottom: 7mm; }
    header span:last-child { grid-column: 2; overflow: hidden;
      text-align: center; text-overflow: ellipsis; white-space: nowrap; }
    main { display: flex; min-height: 0; align-items: flex-start;
      justify-content: center; overflow: hidden; }
    img { display: block; max-width: 100%; max-height: 100%;
      width: auto; height: auto; object-fit: contain; }
    footer { display: flex; align-items: center; justify-content: space-between;
      gap: 12mm; min-width: 0; padding-top: 5mm; }
    footer span:first-child { overflow: hidden; text-overflow: ellipsis;
      white-space: nowrap; }
  `;
  printDocument.head.appendChild(style);

  const header = printDocument.createElement('header');
  const date = printDocument.createElement('span');
  date.textContent = new Date().toLocaleString('pt-BR');
  const title = printDocument.createElement('span');
  title.textContent = fileName;
  header.append(date, title);

  const main = printDocument.createElement('main');
  const image = printDocument.createElement('img');
  image.alt = fileName;
  main.appendChild(image);

  const footer = printDocument.createElement('footer');
  const url = printDocument.createElement('span');
  url.textContent = attachment.data_url;
  const pageCount = printDocument.createElement('span');
  pageCount.textContent = '1/1';
  footer.append(url, pageCount);
  printDocument.body.append(header, main, footer);

  image.src = attachment.data_url;
  try {
    await image.decode();
    if (printFrame !== frame) return;
    frame.contentWindow.focus();
    frame.contentWindow.print();
  } catch {
    frame.remove();
    if (printFrame === frame) printFrame = null;
  }
};
</script>

<template>
  <article
    class="border-b border-[#e0e3e7] bg-white"
    :class="expanded ? '' : 'hover:bg-[#f6f8fc]'"
  >
    <button
      type="button"
      class="flex w-full items-start gap-3 px-4 py-3 text-left"
      @click="emit('toggle')"
    >
      <Avatar :name="fromLabel" :size="36" rounded-full class="flex-shrink-0" />
      <span class="flex min-w-0 flex-1 flex-col">
        <span class="flex items-baseline gap-2 min-w-0">
          <span class="truncate text-sm font-semibold text-n-slate-12">
            {{ fromLabel }}
          </span>
          <span
            v-if="message.status === 'failed'"
            class="flex-shrink-0 rounded-full bg-n-ruby-3 px-2 text-[11px] font-medium text-n-ruby-11"
          >
            {{ t('INBOX.EMAIL.SEND_FAILED') }}
          </span>
          <span class="ml-auto flex-shrink-0 text-xs text-n-slate-10">
            {{ sentAt }}
          </span>
        </span>
        <span
          v-if="expanded && recipientsLabel"
          class="truncate text-xs text-n-slate-10"
        >
          {{ t('INBOX.EMAIL.TO') }} {{ recipientsLabel }}
          <template v-if="message.cc?.length">
            · {{ t('INBOX.EMAIL.CC') }} {{ message.cc.join(', ') }}
          </template>
        </span>
        <span
          v-else-if="!expanded"
          class="truncate text-[13px] text-n-slate-10"
        >
          {{ preview }}
        </span>
      </span>
    </button>

    <div v-if="expanded" class="px-4 pb-4 md:pl-[4.25rem]">
      <div
        ref="bodyRef"
        class="email-message-body overflow-x-auto bg-white text-sm text-n-slate-12"
      >
        <Letter
          :key="`letter-${message.id}-${showQuoted}`"
          class-name="letter-render"
          :html="visibleHtml"
          :allowed-css-properties="letterCss"
        />
      </div>
      <button
        v-if="hasQuotes"
        type="button"
        class="mt-2 inline-flex items-center gap-1 rounded px-1.5 text-xs text-n-slate-11 bg-n-alpha-2"
        :title="
          showQuoted
            ? t('INBOX.EMAIL.HIDE_QUOTED')
            : t('INBOX.EMAIL.SHOW_QUOTED')
        "
        @click="showQuoted = !showQuoted"
      >
        <span class="i-lucide-ellipsis size-4" aria-hidden="true" />
      </button>
      <div v-if="message.attachments?.length" class="mt-4 flex flex-wrap gap-3">
        <button
          v-for="attachment in imageAttachments"
          :key="attachment.id"
          type="button"
          class="group flex w-44 flex-col overflow-hidden rounded-lg border border-[#dadce0] bg-white text-left hover:border-[#16834b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#16834b]"
          :aria-label="`${t('INBOX.EMAIL.OPEN_IMAGE')}: ${attachmentName(attachment)}`"
          @click="previewImage = attachment"
        >
          <img
            :src="attachment.thumb_url || attachment.data_url"
            :alt="attachmentName(attachment)"
            loading="lazy"
            class="h-28 w-full bg-[#f8f9fa] object-contain"
          />
          <span
            class="w-full truncate border-t border-[#e8eaed] px-2 py-1.5 text-xs text-[#3c4043]"
            >{{ attachmentName(attachment) }}</span
          >
        </button>
        <a
          v-for="attachment in otherAttachments"
          :key="attachment.id"
          :href="attachment.data_url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex max-w-full items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-1"
        >
          <span
            class="i-lucide-paperclip size-4 flex-shrink-0 text-n-slate-10"
          />
          <span class="truncate">{{ attachmentName(attachment) }}</span>
        </a>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="previewImage"
        role="dialog"
        aria-modal="true"
        :aria-label="t('INBOX.EMAIL.IMAGE_PREVIEW')"
        class="fixed inset-0 z-[9999] flex flex-col bg-[#202124]/95 text-white"
        @click.self="closePreview"
      >
        <div class="flex h-14 shrink-0 items-center gap-3 px-3 md:px-5">
          <button
            type="button"
            class="inline-flex size-9 shrink-0 items-center justify-center rounded-full hover:bg-white/15"
            :aria-label="t('INBOX.EMAIL.CLOSE_IMAGE')"
            :title="t('INBOX.EMAIL.CLOSE_IMAGE')"
            @click="closePreview"
          >
            <span class="i-lucide-x size-5" aria-hidden="true" />
          </button>
          <span class="min-w-0 flex-1 truncate text-sm">{{
            attachmentName(previewImage)
          }}</span>
          <button
            type="button"
            class="inline-flex size-9 shrink-0 items-center justify-center rounded-full hover:bg-white/15"
            :aria-label="t('INBOX.EMAIL.PRINT_IMAGE')"
            :title="t('INBOX.EMAIL.PRINT_IMAGE')"
            @click="printImage"
          >
            <span class="i-lucide-printer size-5" aria-hidden="true" />
          </button>
          <a
            :href="previewImage.data_url"
            :download="attachmentName(previewImage)"
            class="inline-flex size-9 shrink-0 items-center justify-center rounded-full hover:bg-white/15"
            :aria-label="t('INBOX.EMAIL.DOWNLOAD_IMAGE')"
            :title="t('INBOX.EMAIL.DOWNLOAD_IMAGE')"
            @click.stop
          >
            <span class="i-lucide-download size-5" aria-hidden="true" />
          </a>
        </div>
        <div
          class="flex min-h-0 flex-1 items-center justify-center p-4"
          @click.self="closePreview"
        >
          <img
            :src="previewImage.data_url"
            :alt="attachmentName(previewImage)"
            class="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </Teleport>
  </article>
</template>
